import {
  CalendarStateKey,
  ICalendarEvent,
  TimeFormat,
} from '@pharmaplan/common';
import { DateTime } from 'luxon';
import { groupBy } from 'rambda';
import ICalendar from '../../../types/ICalendar';
import { timeString } from '../../../helpers/Functions';

export const toUTC = (date: string) => {
  const isoDate = DateTime.fromISO(date).toISO();
  const tempDate = new Date(isoDate ?? '');
  return tempDate;
};

/*
  THE CUSTOMTIMEPICKER WAS DESIGNED WITH THE DATE ADAPTER WHICH BY DEFAULT USES
  SYSTEM TIMEZONE. HOWEVER FOR OUR USE-CASE THE UPPER POWERS HAVE DECIDED THAT
  WE SHOULD NOT THINK ABOUT ANY TIMEZONE. SO THIS FUNCTION CONVERTS ISO DATE TO JS DATE
  WHILE PRESERVING THE ABSOLUTE TIME ACROSS TIME ZONES.
*/
export const toJSDate = (date: string) => {
  if (typeof date !== 'string') return date;
  let tempDate = DateTime.fromISO(date, { setZone: true }).toISO({
    includeOffset: false,
  });
  tempDate = `${tempDate?.slice(0, -4)}`;
  return new Date(tempDate);
};

export const eventsMapper = (event: ICalendar) =>
  ({
    ...event,

    end: toJSDate(event.endDate),
    start: toJSDate(event.startDate),
  });

const groupByStartDate = groupBy((e: ICalendar) =>
  e.startDate.split('T')[0]);

const groupByType = groupBy((e: ICalendar) =>
  e.type);

export const transformCalendarEvents = (
  events: ICalendar[],
  activeView: CalendarStateKey,
  timeFormat: TimeFormat,
) => {
  let res: ICalendar[] = [];

  const startGroup = groupByStartDate(events ?? []);

  /* [22-03-2022]:{
      [requested]:{...events}
  } */
  const typeStartGroup = Object.keys(startGroup).reduce(
    (result, x) =>
      ({ ...result, [x]: groupByType(startGroup[x] ?? []) }),
    {},
  );
  // FLAT MAP OF OBJECTS
  const onlyTypeEvents = Object.values(typeStartGroup).map((item) =>
    Object.values(item as ICalendar).map((inItem) => {
      let isMatchingPrev = false;
      let isInWatchlistPrev = false;

      const replacementTypes = [
        ...new Set(
          inItem.map((event: ICalendarEvent) =>
            event?.replacementType),
        ),
      ];
      const isLongTerm = inItem.some(
        (event: ICalendarEvent) =>
          !!event?.longTermShiftContractId,
      );

      const durations = inItem.map((event: ICalendarEvent, index: number) => {
        const {
          startDate,
          endDate,
          logoId,
          id,
          workShiftId,
          availableTimeId,
          isMatchingAvailabilityExist,
          isInWatchlist,
          pharmacyId,
          bookingId,
          paymentStatus,
          isAutoSyncRecord,
          externalPharmacistId,
        } = event ?? {};

        isMatchingPrev = isMatchingPrev || !!isMatchingAvailabilityExist;
        isInWatchlistPrev = isInWatchlistPrev || !!isInWatchlist;

        return {
          label: timeString(startDate, endDate, timeFormat),
          key: `${index}-${startDate}`,
          logoId,
          pharmacyId,
          id,
          isMatchingAvailabilityExist,
          isInWatchlist,
          workshiftId: workShiftId,
          availableTimeId,
          bookingId,
          paymentStatus,
          isAutoSyncRecord,
          externalPharmacistId,
        };
      });

      return {
        ...inItem[0],
        count: inItem.length,
        isMatchingPrev,
        isInWatchlistPrev,
        durations,
        replacementTypes,
        isLongTerm,
      };
    }));

  if (activeView === CalendarStateKey.month) {
    // TRANSFORM TO TRUE JS DATE OBJECTS
    onlyTypeEvents.forEach((item) =>
      item.forEach((e) =>
        res.push(eventsMapper(e))));
  } else {
    res = events.map((e) =>
      eventsMapper(e));
  }
  return { result: res, typeStartGroup };
};
